// src/components/AddressModal/index.js
// ============================================
// UNIFIED ADDRESS MANAGEMENT MODAL
// ============================================
// Features:
// - View all addresses
// - Add new address
// - Edit existing address
// - Delete address
// - Set default address
// - Form validation
// - Loading states
// ============================================

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Plus, Edit2, Trash2, Check, Map, ArrowLeft, AlertCircle, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import MapPicker from '../MapPicker';
import MiniMap from './MiniMap';
import { calculateDistance } from '../../utils/storeUtils';
import './index.css';
import '../ProfileSectionPage/index.css';

const AddressModal = ({
  isOpen,
  onClose,
  addresses = [],
  selectedAddress = null,
  onSelectAddress,
  onCreateAddress,
  onUpdateAddress,
  onDeleteAddress,
  onSetDefaultAddress,
  loading = false,
  operationInProgress = false,
  isPage = false,
  selectedStore = null
}) => {
  const navigate = useNavigate();

  // Form state
  const [mode, setMode] = useState('list'); // 'list', 'add', 'edit'
  const [showMap, setShowMap] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    recipient_name: '',
    latitude: '',
    longitude: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode('list');
      setEditingAddress(null);
      resetForm();
    }
  }, [isOpen]);

  // ============================================
  // FORM HELPERS
  // ============================================
  const resetForm = () => {
    setFormData({
      label: '',
      address: '',
      apartment: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      recipient_name: '',
      latitude: '',
      longitude: ''
    });
    setFormErrors({});
    setSubmitAttempted(false);
  };

  const validateForm = () => {
    const errors = {};

    // Label validation
    if (!formData.label.trim()) {
      errors.label = 'Label is required';
    }

    // Address validation
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    } else if (!formData.latitude && formData.address.trim().length < 10) {
      // Only enforce 10-char minimum if no map coordinates are set
      errors.address = 'Address must be at least 10 characters';
    } else if (formData.address.trim().length < 3) {
      errors.address = 'Address is too short';
    }

    // City validation
    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    // State validation
    if (!formData.state.trim()) {
      errors.state = 'State is required';
    }

    // ZIP code validation
    if (!formData.zip_code.trim()) {
      errors.zip_code = 'ZIP code is required';
    } else if (!/^\d{6}$/.test(formData.zip_code)) {
      errors.zip_code = 'ZIP code must be 6 digits';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = 'Phone must be 10 digits';
    }

    // Recipient Name validation
    if (!formData.recipient_name.trim()) {
      errors.recipient_name = 'Recipient Name is required';
    }

    // Apartment validation
    if (!formData.apartment.trim()) {
      errors.apartment = 'Apartment/Flat No is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleInputChange = (field, value) => {
    // Special handling for numeric fields
    if (field === 'zip_code') {
      value = value.replace(/\D/g, '').slice(0, 6);
    } else if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field if it was previously invalid
    if (submitAttempted && formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddClick = () => {
    resetForm();
    setMode('add');
  };

  const handleEditClick = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label || '',
      address: address.address || '',
      apartment: address.apartment || '',
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || address.post_code || '',
      phone: address.phone || '',
      recipient_name: address.recipient_name || '',
      latitude: address.latitude || '',
      longitude: address.longitude || ''
    });
    setMode('edit');
  };

  const handleCancelEdit = () => {
    setMode('list');
    setEditingAddress(null);
    resetForm();
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    if (!validateForm()) {
      console.log('❌ Form validation failed:', formErrors);
      return;
    }

    // No real map pin captured (geolocation denied/failed, or the customer
    // never touched the map) — warn before saving, since delivery-fee
    // estimates and self-managed delivery depend on a real location.
    if (!formData.latitude || !formData.longitude) {
      const proceedAnyway = window.confirm(
        "We couldn't confirm your exact location on the map. This may affect delivery fee accuracy. " +
        'Tap Cancel to go back and set your location on the map, or OK to save this address anyway.'
      );
      if (!proceedAnyway) return;
    }

    const result = mode === 'add'
      ? await onCreateAddress(formData)
      : await onUpdateAddress(editingAddress.id, formData);

    if (result.success) {
      // Success - return to list
      setMode('list');
      setEditingAddress(null);
      resetForm();
    } else {
      // Show error (handled by parent component)
      console.error('❌ Operation failed:', result.error);
    }
  };

  const handleDelete = async (addressId) => {
    if (addresses.length <= 1) {
      alert('Cannot delete the last address. You must have at least one address.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this address?');
    if (!confirmed) return;

    const result = await onDeleteAddress(addressId);

    if (!result.success) {
      alert(result.error || 'Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    const result = await onSetDefaultAddress(addressId);

    if (result.success && onSelectAddress) {
      const address = addresses.find(addr => addr.id === addressId);
      if (address) {
        onSelectAddress(address);
      }
    }
  };

  const handleSelectAndClose = (address) => {
    if (onSelectAddress) {
      onSelectAddress(address);
    }
    onClose();
  };

  const handleMapConfirm = (locationData) => {
    // Auto-populate form fields from reverse geocoded data
    setFormData(prev => ({
      ...prev,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      // Only overwrite fields that are empty OR if user explicitly picked from map
      address: locationData.address || prev.address,
      apartment: locationData.apartment || prev.apartment,
      city: locationData.city || prev.city,
      state: locationData.state || prev.state,
      zip_code: locationData.zip_code || prev.zip_code,
    }));
    setShowMap(false);
  };

  // If there's some external dependency managing isOpen and it needs to be hidden, we should handle that in the parent or allow it to return null if not isOpen. Since it's now a page, we don't need `if (!isOpen) return null;`

  // ============================================
  // RENDER
  // ============================================

  if (!isOpen && !isPage) return null;

  const titleContent = (
    <>
      {mode === 'add' && 'Add New Address'}
      {mode === 'edit' && 'Edit Address'}
      {mode === 'list' && 'Manage Addresses'}
    </>
  );

  const innerContent = (
    <div className={isPage ? "profile-section-content" : "address-modal-content"}>
      {/* Loading State — same pattern as My Orders/My Transactions'
          centered loader (Loader icon + "Loading X..." text, min-height
          box so it's centered in the actual page area, not just within
          its own small content box). */}
      {loading && (
        <div className="addresses-loading">
          <Loader size={40} className="addresses-spinner" />
          <p>Loading addresses...</p>
        </div>
      )}

      {/* List Mode */}
      {!loading && mode === 'list' && (
        <>
          <button className="add-new-button" onClick={handleAddClick}>
            <Plus size={18} />
            <span>Add New Address</span>
          </button>

          {addresses.length === 0 ? (
            <div className="no-addresses">
              <p>No addresses saved yet</p>
              <button onClick={handleAddClick}>Add Your First Address</button>
            </div>
          ) : (
            <div className="addresses-list">
              {addresses.map(addr => {
                let isOutOfRadius = false;
                let addressDistance = 0;
                let maxRadius = 50;

                if (selectedStore?.latitude && selectedStore?.longitude && addr.latitude && addr.longitude) {
                  // eslint-disable-next-line no-unused-vars
                  addressDistance = calculateDistance(
                    parseFloat(selectedStore.latitude),
                    parseFloat(selectedStore.longitude),
                    parseFloat(addr.latitude),
                    parseFloat(addr.longitude)
                  );
                  // eslint-disable-next-line no-unused-vars
                  maxRadius = parseFloat(selectedStore.delivery_radius) || 50;
                  isOutOfRadius = false; // Application available all over the country
                }

                return (
                  <div key={addr.id} className={`address-card ${isOutOfRadius ? 'address-out-of-radius' : ''}`}>
                    {/* Header */}
                    <div className="address-card-header">
                      <div className="address-label-section">
                        <MapPin size={18} />
                        <strong>{addr.label}</strong>
                      </div>
                      {addr.is_default && (
                        <span className="default-badge">Default</span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="address-card-body">
                      <p className="address-text">
                        {addr.address}
                        {addr.apartment && `, ${addr.apartment}`}
                      </p>
                      <p className="address-text">
                        {addr.city && `${addr.city},`} {addr.state && `${addr.state},`} {addr.post_code || addr.zip_code}
                      </p>
                      {addr.recipient_name && (
                        <p className="address-recipient">Recipient: {addr.recipient_name}</p>
                      )}
                      {addr.phone && (
                        <p className="address-phone">Phone: {addr.phone}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="address-card-actions">
                      {!addr.is_default && (
                        <button
                          className="action-button primary"
                          onClick={() => handleSetDefault(addr.id)}
                          disabled={operationInProgress}
                        >
                          <Check size={16} />
                          Set as Default
                        </button>
                      )}

                      <button
                        className="action-button secondary"
                        onClick={() => handleEditClick(addr)}
                        disabled={operationInProgress}
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>

                      {addresses.length > 1 && (
                        <button
                          className="action-button danger"
                          onClick={() => handleDelete(addr.id)}
                          disabled={operationInProgress}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      )}

                      {addr.id === selectedAddress?.id ? (
                        <button className="action-button success" disabled>
                          <Check size={16} />
                          Selected
                        </button>
                      ) : (
                        <button
                          className="action-button primary"
                          onClick={() => handleSelectAndClose(addr)}
                          disabled={isOutOfRadius}
                        >
                          Select & Close
                        </button>
                      )}
                    </div>

                    {/* Out of range warning */}
                    {isOutOfRadius && (
                      <div className="address-out-of-range-warning">
                        <AlertCircle size={14} />
                        <span>Does not deliver to your location</span>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Mode */}
      {(mode === 'add' || mode === 'edit') && (
        <div className="add-address-form">
          <button className="back-button" onClick={handleCancelEdit}>
            <ArrowLeft size={18} />
            <span>Back to List</span>
          </button>

          <div className="desktop-form-grid">
            <div className="desktop-form-col">
              {/* Contact Information Section */}
              <div className="form-section">
                <h3 className="form-section-title">Contact Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Recipient Name *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe"
                      value={formData.recipient_name}
                      onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                      className={`form-input ${formErrors.recipient_name ? 'error' : ''}`}
                      disabled={operationInProgress}
                    />
                    {formErrors.recipient_name && (
                      <span className="form-error">{formErrors.recipient_name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      placeholder="10-digit number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`form-input ${formErrors.phone ? 'error' : ''}`}
                      maxLength={10}
                      disabled={operationInProgress}
                    />
                    {formErrors.phone && (
                      <span className="form-error">{formErrors.phone}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Location on Map Section */}
              <div className="form-section">
                <h3 className="form-section-title">Location on Map</h3>

                <div className="form-group">
                  <label className="form-label">
                    Place Label * <span className="form-hint">(e.g., Home, Office, Parents)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Where should we deliver?"
                    value={formData.label}
                    onChange={(e) => handleInputChange('label', e.target.value)}
                    className={`form-input ${formErrors.label ? 'error' : ''}`}
                    disabled={operationInProgress}
                  />
                  {formErrors.label && (
                    <span className="form-error">{formErrors.label}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Search on Map</label>
                  <MiniMap
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    onLocationChange={(data) => {
                      setFormData(prev => ({
                        ...prev,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        address: data.address || prev.address,
                        apartment: data.apartment || prev.apartment,
                        city: data.city || prev.city,
                        state: data.state || prev.state,
                        zip_code: data.zip_code || prev.zip_code,
                      }));
                    }}
                    onExpandMap={() => setShowMap(true)}
                  />
                  {!formData.latitude && !formData.longitude && (
                    <div className="form-hint-warning">
                      <AlertCircle size={14} />
                      <span>
                        We couldn't confirm your exact location. Tap the map above (or drag the pin)
                        to set it precisely — this keeps your delivery fee accurate.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="desktop-form-col">
              {/* Address Details Section */}
              <div className="form-section address-details-section">
                <h3 className="form-section-title">Address Details</h3>

                <div className="form-group">
                  <label className="form-label">Street Address *</label>
                  <input
                    type="text"
                    placeholder="House No, Building, Area"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={`form-input ${formErrors.address ? 'error' : ''}`}
                    disabled={operationInProgress}
                  />
                  {formErrors.address && (
                    <span className="form-error">{formErrors.address}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Apartment/Flat No *
                  </label>
                  <input
                    type="text"
                    placeholder="Floor, Suite, Landmark"
                    value={formData.apartment}
                    onChange={(e) => handleInputChange('apartment', e.target.value)}
                    className={`form-input ${formErrors.apartment ? 'error' : ''}`}
                    disabled={operationInProgress}
                  />
                  {formErrors.apartment && (
                    <span className="form-error">{formErrors.apartment}</span>
                  )}
                </div>

                <div className="form-row three-cols">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      placeholder="City"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={`form-input ${formErrors.city ? 'error' : ''}`}
                      disabled={operationInProgress}
                    />
                    {formErrors.city && (
                      <span className="form-error">{formErrors.city}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      placeholder="State"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={`form-input ${formErrors.state ? 'error' : ''}`}
                      disabled={operationInProgress}
                    />
                    {formErrors.state && (
                      <span className="form-error">{formErrors.state}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">ZIP *</label>
                    <input
                      type="text"
                      placeholder="ZIP Code"
                      value={formData.zip_code}
                      onChange={(e) => handleInputChange('zip_code', e.target.value)}
                      className={`form-input ${formErrors.zip_code ? 'error' : ''}`}
                      maxLength={6}
                      disabled={operationInProgress}
                    />
                    {formErrors.zip_code && (
                      <span className="form-error">{formErrors.zip_code}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className="save-address-button"
            onClick={handleSubmit}
            disabled={operationInProgress}
          >
            {operationInProgress ? (
              <>
                <div className="button-spinner"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Plus size={18} />
                <span>{mode === 'add' ? 'Add Address' : 'Update Address'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  if (isPage) {
    return (
      <>
        <div className="profile-section-header">
          <div className="profile-section-header-inner">
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', padding: 0, marginRight: '15px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--color-primary, #ff6b35)' }}
              title="Go Back"
            >
              <ArrowLeft size={28} />
            </button>
            <MapPin size={28} />
            <h2>{titleContent}</h2>
          </div>
        </div>
        {innerContent}
        {showMap && (
          <MapPicker
            initialLocation={
              formData.latitude && formData.longitude
                ? { lat: formData.latitude, lng: formData.longitude }
                : null
            }
            onConfirm={handleMapConfirm}
            onClose={() => setShowMap(false)}
          />
        )}
      </>
    );
  }

  return createPortal(
    <div className="address-modal-overlay" onClick={onClose}>
      <div className="address-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="address-modal-header">
          <div className="address-modal-title-container">
            <MapPin size={24} color="#ff6b6b" />
            <h2>{titleContent}</h2>
          </div>
          <button onClick={onClose} className="address-modal-close">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        {innerContent}
      </div>
      {showMap && (
        <MapPicker
          initialLocation={
            formData.latitude && formData.longitude
              ? { lat: formData.latitude, lng: formData.longitude }
              : null
          }
          onConfirm={handleMapConfirm}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>,
    document.body
  );
};

export default AddressModal;